/**
 * MODULE: domain/pages/frontend/components/editor/PagesCrmCompanyPreviewBlockView.tsx
 * PURPOSE: Live CRM company/account snapshot card block (Sprint 9)
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';
import { useCrmEntityPreview } from '../../hooks/useCrmEntityPreview';
import { crmMentionPath } from '../../utils/crmMentionUtils';
import type { PageCompanyPreview } from '../../../contracts/pages.types';
import { CrmPreviewBlockShell } from './CrmPreviewBlockShell';

export function PagesCrmCompanyPreviewBlockView({ node, selected }: NodeViewProps) {
  const navigate = useNavigate();
  const isPublicView = useIsPagesPublicView();
  const companyId = String(node.attrs.companyId ?? '');
  const fallbackLabel = String(node.attrs.companyLabel ?? 'Untitled account').trim() || 'Untitled account';
  const { data: rawData, isLoading, isError, refetch, isFetching } = useCrmEntityPreview(
    'company',
    companyId || null,
    { enabled: !isPublicView }
  );
  const data = rawData as PageCompanyPreview | undefined;

  const metaParts = isPublicView
    ? []
    : [
        data?.industry,
        data?.website,
        data?.employee_count != null ? `${data.employee_count} employees` : null,
      ].filter(Boolean);
  const metaLine = isPublicView
    ? 'Saved account reference from the shared page'
    : metaParts.join(' · ') || 'Open in CRM for full details';

  return (
    <NodeViewWrapper className="my-3">
      <CrmPreviewBlockShell
        testIdPrefix="pages-crm-company-preview"
        selected={selected}
        ringClass="ring-teal-400"
        borderClass="border-teal-200 dark:border-teal-900/50"
        gradientClass="from-teal-50 to-white dark:from-teal-950/30 dark:to-gray-900"
        icon={
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-200">
            <HiOutlineOfficeBuilding className="h-5 w-5" aria-hidden />
          </span>
        }
        badgeLabel="CRM account"
        badgeClassName="bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200"
        title={isPublicView ? fallbackLabel : (data?.name ?? fallbackLabel)}
        meta={metaLine}
        isLoading={!isPublicView && isLoading}
        isError={!isPublicView && isError}
        errorHint="Could not load live account data. Showing saved label."
        isFetching={!isPublicView && isFetching}
        entityId={companyId}
        onRefresh={() => void refetch()}
        onOpenCrm={() => companyId && navigate(crmMentionPath('company', companyId))}
        publicReadOnly={isPublicView}
      />
    </NodeViewWrapper>
  );
}
